/*
  Premade cart refresh (<trc-premade-cart-refresh>) for the cart drawer and the cart page.

  Checkout (the app's trc-premade-deck-validation Function) refuses a premade
  line whose "_TRC deck version" is not the variant's current
  custom.premade_deck_revision, or whose "_TRC reorder" does not match
  custom.premade_deck_reorder. Lines added before a deck was updated, restocked
  or sold out hit that wall. snippets/trc-premade-cart-refresh.liquid renders
  this element on every premade line and decides from those same metafields:
    data-state="stale"    the Function would refuse the line: show the notice
    data-state="current"  the line passes: nothing to do
    data-state="unknown"  no revision metafield to compare: ask the Worker
                          (once per variant per page) and compare with it

  "Update it in your cart" asks the Worker for the current version, builds the
  line's properties with TRCPremadeVersions.lineProperties (the same code the
  product forms use; a pin is never made up here) and swaps them in with one
  /cart/change.js call, with the quantity from a fresh read of the cart.
  Success is "a cart line carries the new properties", whatever its key. A
  refused or unapplied change leaves the line as it is: the notice links to the
  deck page and the cart is redrawn from a fresh read. A line is never removed
  on its own.

  The app's validation Functions only refuse at checkout (never on cart
  changes), so one line can be updated while another is still outdated. If a
  change is refused anyway, the line stays and the notice links to the deck page.

  Fireball Foil decks and lines with "Two-sided cards" carry per-card choices
  made on the deck page, so they are never refreshed here: the notice links to
  the deck page.
*/
(() => {
  'use strict';
  if (customElements.get('trc-premade-cart-refresh')) return;

  const PIN = '_TRC deck version';
  const REORDER = '_TRC reorder';
  const PROFILE = '_Reorder profile';
  const TWO_SIDED = 'Two-sided cards';
  const REORDER_KEYS = ['_Input method', REORDER, '_Reorder decklist URL', PROFILE];
  const UNMANAGED_KEY = 'trc-premade-refresh:unmanaged';
  const COPY = {
    stale: 'This deck was updated.',
    button: 'Update it in your cart',
    busy: 'Updating...',
    changed: 'See what changed',
    failed: 'We could not update this deck here.',
    pending: 'This deck is being updated. Try again in a minute.',
    soldOut: 'This deck is out of stock right now. Remove it from your cart to check out.',
    manual: 'This deck was updated. Remove it from your cart and add it again from its page.',
    link: 'Open the deck page',
    reload: 'Your deck was updated. Reload the page to see your cart.',
  };
  // Card set lines (data-noun="set", snippets/trc-product-noun.liquid) say "set" where decks say "deck".
  const SET_COPY = Object.fromEntries(Object.entries(COPY).map(([key, text]) => [key, text.replace(/\bdeck\b/g, 'set').replace(/\bDeck\b/g, 'Set')]));
  const checks = new Map();
  const notes = new Map();
  let loading = null;

  const routes = () => window.FoxTheme?.routes || {};
  const root = () => window.Shopify?.routes?.root || '/';
  const cartUrl = name => `${routes()[`cart_${name}_url`] || `${root()}cart/${name}`}.js`;
  const cartJs = () => `${routes().cart_url || `${root()}cart`}.js`;

  function storageRead(key, fallback) {
    try { const value = JSON.parse(window.sessionStorage.getItem(key) || 'null'); return value ?? fallback; } catch (_) { return fallback; }
  }
  function storageWrite(key, value) {
    try { if (value == null) window.sessionStorage.removeItem(key); else window.sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* per-page memory still works */ }
  }
  // Variants the Worker says are not managed: no need to ask again this session.
  const unmanaged = () => new Set(storageRead(UNMANAGED_KEY, []).map(String));
  function rememberAnswer(variantId, data) {
    const set = unmanaged();
    if (data?.managed === false) set.add(variantId); else set.delete(variantId);
    storageWrite(UNMANAGED_KEY, [...set].slice(-200));
  }

  // Deck page link (locale prefixes such as /fr/ or /en-ca/ allowed), same site only.
  function deckHref(raw, variantId) {
    try {
      const url = new URL(raw || '', location.origin);
      if (url.origin !== location.origin || !/^(?:\/[a-z]{2}(?:-[a-z]{2,4})?)?\/products\/[^/]+\/?$/i.test(url.pathname)) return '';
      url.search = ''; url.hash = '';
      if (/^\d+$/.test(String(variantId || ''))) url.searchParams.set('variant', String(variantId));
      return url.pathname + url.search;
    } catch (_) { return ''; }
  }
  function anchor(href, text, className) {
    const link = document.createElement('a');
    link.href = href; link.textContent = text; link.className = `link ${className}`;
    return link;
  }

  // trc-premade-versions.js reads and checks the Worker answer. Product pages
  // already load it; elsewhere it is fetched the first time a line needs it.
  function versions(src) {
    if (window.TRCPremadeVersions?.read && window.TRCPremadeVersions?.lineProperties) return Promise.resolve(window.TRCPremadeVersions);
    if (!loading) {
      loading = new Promise((resolve, reject) => {
        const finish = () => (window.TRCPremadeVersions?.read ? resolve(window.TRCPremadeVersions) : reject(new Error('versions')));
        let script = [...document.scripts].find(element => /\/trc-premade-versions(?:\.[^/]*)?\.js/.test(element.src || ''));
        if (!script) {
          if (!src) { reject(new Error('versions')); return; }
          script = document.createElement('script');
          script.src = src;
          script.async = true;
          document.head.append(script);
        }
        script.addEventListener('load', finish, {once: true});
        script.addEventListener('error', () => reject(new Error('versions')), {once: true});
        setTimeout(finish, 15000);
      }).catch(error => { loading = null; throw error; });
    }
    return loading;
  }

  // A fresh Worker answer, which also replaces the per-page answer for the variant.
  async function freshAnswer(api, variantId) {
    const data = await api.read(variantId);
    checks.set(variantId, Promise.resolve(data));
    rememberAnswer(variantId, data);
    return data;
  }

  function groupedSections() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {bubbles: true, detail: {sections}}));
    return sections.filter((id, index) => id && sections.indexOf(id) === index);
  }

  async function post(name, body) {
    const response = await fetch(cartUrl(name), {
      method: 'POST', credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(body),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result || result.status || result.errors) throw new Error(result?.description || 'cart');
    return result;
  }

  async function getCart() {
    const response = await fetch(cartJs(), {credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'}});
    if (!response.ok) throw new Error('cart');
    const cart = await response.json();
    if (!Array.isArray(cart?.items)) throw new Error('cart');
    return cart;
  }

  async function withSections(cart, sections) {
    if (!sections.length) return cart;
    const response = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(sections.join(','))}`, {credentials: 'same-origin', headers: {Accept: 'application/json'}});
    if (response.ok) cart.sections = await response.json();
    return cart;
  }

  // Redraws the cart sections from `cart`. Without sections only a reload shows
  // the change, so `reload` says whether that is wanted.
  function publish(cart, reload) {
    const theme = window.FoxTheme;
    if (theme?.pubsub?.PUB_SUB_EVENTS && cart?.sections) theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {cart});
    else if (reload) window.location.reload();
  }

  // The Function reads "" and "false" as stocked and "true" as a print-run line.
  const reorderOf = value => {
    const raw = String(value ?? '').toLowerCase();
    return raw === '' || raw === 'false' ? false : raw === 'true' ? true : null;
  };

  const cleanProperties = properties => {
    const clean = {};
    for (const [name, value] of Object.entries(properties && typeof properties === 'object' && !Array.isArray(properties) ? properties : {})) {
      if (value != null && String(value) !== '') clean[name] = String(value);
    }
    return clean;
  };
  const sameProperties = (a, b) => {
    const left = cleanProperties(a), right = cleanProperties(b);
    const keys = Object.keys(left);
    return keys.length === Object.keys(right).length && keys.every(name => right[name] === left[name]);
  };
  const matches = (item, variantId, properties) => String(item?.variant_id) === String(variantId) && sameProperties(item.properties, properties);
  // Updated: a line carries the new properties and the old line (its key) is gone.
  const updated = (items, lineKey, variantId, properties) => Array.isArray(items) &&
    items.some(item => matches(item, variantId, properties)) && !items.some(item => item.key === lineKey);

  // The line's properties for the current version: everything else it carries
  // stays, the pin and the print-run markers come from lineProperties. A
  // print-run line keeps its own "_Reorder profile" only while it stays one.
  function nextProperties(api, data, current, reorderEnabled) {
    const pinned = api.lineProperties(data, reorderEnabled);
    if (!pinned || !pinned[PIN]) return null;
    const next = {};
    for (const [name, value] of Object.entries(cleanProperties(current))) {
      if (name !== PIN && !REORDER_KEYS.includes(name)) next[name] = value;
    }
    Object.assign(next, pinned);
    const profile = String(current?.[PROFILE] ?? '').trim();
    if (PROFILE in pinned && reorderOf(current?.[REORDER]) === true && /^[A-Za-z][A-Za-z0-9 _-]{0,39}$/.test(profile)) next[PROFILE] = profile;
    return next;
  }

  class PremadeCartRefresh extends HTMLElement {
    get copy() { return this.dataset.noun === 'set' ? SET_COPY : COPY; }
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.variantId = String(this.dataset.variantId || '');
      this.lineKey = String(this.dataset.lineKey || '');
      this.reorderEnabled = this.dataset.reorderEnabled === 'true';
      let properties;
      try { properties = JSON.parse(this.querySelector('[data-trc-line-properties]')?.textContent || '{}'); } catch (_) { properties = {}; }
      this.properties = cleanProperties(properties);
      this.oneClick = this.dataset.oneClick !== 'false' && !(TWO_SIDED in this.properties);
      if (!/^\d+$/.test(this.variantId) || !this.lineKey) return;
      const note = notes.get(this.lineKey);
      if (note) { this.show(); this.fallback(note); return; }
      if (this.dataset.state === 'stale') this.show();
      else if (this.dataset.state === 'unknown') this.check();
    }

    // No revision metafield to compare with: the Worker's answer decides, once per variant per page.
    async check() {
      if (unmanaged().has(this.variantId)) return;
      try {
        const api = await versions(this.dataset.versionsSrc);
        if (!checks.has(this.variantId)) {
          const request = api.read(this.variantId);
          checks.set(this.variantId, request);
          request.then(data => rememberAnswer(this.variantId, data), () => checks.delete(this.variantId));
        }
        const data = await checks.get(this.variantId);
        if (!this.isConnected || !data?.managed) return;
        if (this.properties[PIN] !== data.active.revision || reorderOf(this.properties[REORDER]) !== data.isReorder) this.show();
      } catch (_) { /* Not known: say nothing rather than guess. */ }
    }

    show() {
      this.dataset.stale = 'true';
      const notice = document.createElement('div');
      notice.className = 'trc-premade-refresh__notice';
      notice.setAttribute('role', 'status');
      const text = document.createElement('p');
      text.className = 'trc-premade-refresh__text';
      const actions = document.createElement('div');
      actions.className = 'trc-premade-refresh__actions';
      const href = deckHref(this.dataset.productUrl, this.variantId);
      if (this.oneClick) {
        text.textContent = this.copy.stale;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn--secondary btn--small trc-premade-refresh__button';
        button.textContent = this.copy.button;
        button.addEventListener('click', event => { event.preventDefault(); this.update(button); });
        actions.append(button);
        if (href) actions.append(anchor(href, this.copy.changed, 'trc-premade-refresh__changed'));
      } else {
        text.textContent = this.copy.manual;
        if (href) actions.append(anchor(href, this.copy.link, 'trc-premade-refresh__link'));
      }
      notice.append(text, actions);
      this.notice = notice;
      this.replaceChildren(...[...this.children].filter(child => child.matches('script')), notice);
      this.hidden = false;
    }

    // Replaces the notice with `message` and a deck page link; with `retry`
    // the update button stays. A failure survives the cart being redrawn
    // (same line key); a retry does not.
    fallback(message, retry = false) {
      if (!this.notice) return;
      if (retry) notes.delete(this.lineKey);
      else notes.set(this.lineKey, message);
      this.dataset.stale = retry ? 'true' : 'failed';
      const text = this.notice.querySelector('.trc-premade-refresh__text');
      const actions = this.notice.querySelector('.trc-premade-refresh__actions');
      text.textContent = message;
      actions.replaceChildren();
      if (retry && this.oneClick) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn--secondary btn--small trc-premade-refresh__button';
        button.textContent = this.copy.button;
        button.addEventListener('click', event => { event.preventDefault(); this.update(button); });
        actions.append(button);
      }
      const href = deckHref(this.dataset.productUrl, this.variantId);
      if (href) actions.append(anchor(href, this.copy.link, 'trc-premade-refresh__link'));
    }

    async start(button) {
      if (this.busy) return false;
      this.busy = true;
      this.label = button.textContent;
      button.disabled = true;
      button.textContent = this.copy.busy;
      return true;
    }
    finish(button) {
      this.busy = false;
      if (button.isConnected) { button.disabled = false; button.textContent = this.label; }
    }

    // Shows a plain message in the notice, without buttons or links.
    say(message) {
      if (!this.notice) return;
      this.notice.querySelector('.trc-premade-refresh__text').textContent = message;
      this.notice.querySelector('.trc-premade-refresh__actions').replaceChildren();
    }

    // The line is updated: redraw the cart from `cart`, fetching its sections
    // when it has none. If that fails, say so instead of leaving a stale notice.
    async done(cart, sections) {
      notes.delete(this.lineKey);
      try { publish(cart.sections ? cart : await withSections(cart, sections), true); } catch (_) { this.say(this.copy.reload); }
    }

    async update(button) {
      if (!await this.start(button)) return;
      const sections = groupedSections();
      try {
        let api, data, cart;
        try {
          api = await versions(this.dataset.versionsSrc);
          data = await freshAnswer(api, this.variantId);
        } catch (_) { this.fallback(this.copy.failed, true); return; }
        if (!data?.managed) {
          // Not a managed deck: checkout does not check it. A line flagged from
          // its revision metafield waits for the Worker to catch up.
          if (this.dataset.state === 'unknown') { notes.delete(this.lineKey); this.hidden = true; this.dataset.stale = 'false'; }
          else this.fallback(this.copy.pending, true);
          return;
        }
        if (data.isReorder && !this.reorderEnabled) { this.fallback(this.copy.soldOut); return; }
        try { cart = await getCart(); } catch (_) { this.fallback(this.copy.failed, true); return; }
        const line = cart.items.find(item => item.key === this.lineKey);
        const next = nextProperties(api, data, line ? line.properties : this.properties, this.reorderEnabled);
        if (!next) { this.fallback(this.copy.failed); return; }
        if (!line) {
          // Already updated (another tab, an earlier click): just redraw.
          if (updated(cart.items, this.lineKey, this.variantId, next)) await this.done(cart, sections);
          else this.fallback(this.copy.failed);
          return;
        }
        if (sameProperties(next, line.properties)) { this.fallback(this.copy.pending, true); return; }
        try {
          const result = await post('change', {id: this.lineKey, quantity: line.quantity, properties: next, ...(sections.length ? {sections, sections_url: window.location.pathname} : {})});
          if (updated(result.items, this.lineKey, this.variantId, next)) { await this.done(result, sections); return; }
        } catch (_) { /* refused: the fresh cart below decides */ }
        // Refused, or answered without the change: the fresh cart decides.
        let fresh;
        try { fresh = await getCart(); } catch (_) { this.fallback(this.copy.failed, true); return; }
        if (updated(fresh.items, this.lineKey, this.variantId, next)) { await this.done(fresh, sections); return; }
        this.fallback(this.copy.failed);
        try { publish(await withSections(fresh, sections), false); } catch (_) { /* the notice stays */ }
      } finally { this.finish(button); }
    }
  }

  customElements.define('trc-premade-cart-refresh', PremadeCartRefresh);
  window.TRCPremadeCartRefresh = {reorderOf, sameProperties, deckHref};
})();
